import { DatePipe, NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { DurationPipe } from '@core/pipes/duration.pipe';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { PercentagePipe } from '@core/pipes/percentage.pipe';
import { EMatchingType } from '@enums/campaign-analytics.enum';
import { EPlatformFeature } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICampaignExportApi } from '@pages/campaigns/campaign-details/campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { CarouselComponent } from '@shared/components/carousel/carousel.component';
import { ICarouselConfiguration } from '@shared/components/carousel/carousel.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { MediaUtilitiesService } from '@utilities/media-utilities.service';
import { TextClampUtilitiesService } from '@utilities/text-clamp-utilites.service';
import { differenceInCalendarDays } from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ITileConfiguration } from './social-content-tile.interface';
import { SocialContentTileService } from './social-content-tile.service';

@Component({
  imports: [
    CarouselComponent,
    IconBadgeComponent,
    MatTooltipModule,
    NumberSuffixPipe,
    SocialBadgeComponent,
    StatusBadgeComponent,
    TranslateModule,
    DurationPipe,
    NgClass,
    PercentagePipe,
    NgxSkeletonLoaderModule,
    TextClampTooltipDirective,
    IconComponent,
  ],
  selector: 'app-social-content-tile',
  styleUrl: './social-content-tile.component.scss',
  templateUrl: './social-content-tile.component.html',
})
export class SocialContentTileComponent {
  public readonly tileConfiguration = input.required<ITileConfiguration>();

  public readonly clickable = input<boolean>(true);
  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly briefingOverview = input<boolean>(false);
  public readonly briefingTimeline = input<boolean>(false);
  public readonly exportMode = input<boolean>(false);
  public readonly campaignExport = input<ICampaignExportApi | null>(null);
  public readonly loading = input<boolean>(false);
  public readonly dialogMode = input<boolean>(false);
  public readonly noCampaignContextMode = input<boolean>(false);

  public readonly tileClicked = output<ITileConfiguration>();

  public readonly EMatchingType = EMatchingType;
  public readonly EPlatformFeature = EPlatformFeature;
  public readonly EExecutionType = EExecutionType;

  public readonly creatorNameElement = viewChild<ElementRef<HTMLElement>>('creatorName');
  public readonly titleElement = viewChild<ElementRef<HTMLElement>>('title');
  public readonly descriptionElement = viewChild<ElementRef<HTMLElement>>('description');

  public readonly isCreatorNameClamped = signal<boolean>(false);
  public readonly isTitleClamped = signal<boolean>(false);
  public readonly isDescriptionClamped = signal<boolean>(false);

  public readonly platformFeatureCardColor = computed<string>(() => {
    return 'var(--shikenso-gray-base)';
  });

  public readonly platformFeatureHeaderBackgroundColor = computed<string>(() => {
    return this.socialContentTileService.getPlatformFeatureTileColor(this.tileConfiguration().platformFeature, 0.25);
  });

  public readonly platformFeatureHeaderBorderColor = computed<string>(() => {
    return this.socialContentTileService.getPlatformFeatureTileColor(this.tileConfiguration().platformFeature);
  });

  public readonly platformFeatureIconColor = computed<string>(() => {
    return this.socialContentTileService.getPlatformFeatureSocialColor(this.tileConfiguration().platformFeature);
  });

  public readonly creatorNameColor = computed<string>(() => {
    return 'var(--shikenso-purple-base)';
  });

  public readonly executionDateTimeFormatted = computed(() => {
    return this.tileConfiguration().executionDateTime
      ? this.dateUtils.parseDateTimeFromApi(this.tileConfiguration().executionDateTime ?? '')
      : null;
  });

  public readonly carouselConfiguration = computed<ICarouselConfiguration>((): ICarouselConfiguration => {
    const media = this.tileConfiguration().media ?? [];

    return {
      autoPlay: false,
      items: media.map((imageUrl: string, index: number) => ({
        alt: `${this.tileConfiguration().creator.name} - Media ${index + 1}`,
        isYoutubeContent:
          this.tileConfiguration().platformFeature === EPlatformFeature.youtubeShort ||
          this.tileConfiguration().platformFeature === EPlatformFeature.youtubeVod,
        mediaType: this.mediaUtils.getMediaType(imageUrl),
        src: imageUrl,
      })),
      maxHeight: 464,
      showControls: true,
      showIndicators: true,
      videoPlayedContext: this.noCampaignContextMode()
        ? { eventType: EEventType.creatorContentVideoPlayed }
        : { analyticsPopupActive: this.dialogMode(), eventType: EEventType.campaignAnalyticsContentVideoPlayed },
      width: 232,
    };
  });

  public readonly firstMediaType = computed(() => {
    const images = this.tileConfiguration().media ?? [];

    return images.length > 0 ? this.mediaUtils.getMediaType(images[0]) : 'image';
  });

  public readonly canDisplayAnyKpi = computed(
    () =>
      !this.creatorBriefingMode() &&
      (!this.exportMode() || this.campaignExport()?.allKpisSelected || this.campaignExport()?.kpis?.length),
  );

  public readonly canDisplayKpiStatus = computed(
    () => !this.creatorBriefingMode() && (!this.exportMode() || this.campaignExport()?.showKpiStatus),
  );

  public readonly canDisplayDeliverableStatus = computed(
    () => !this.exportMode() || this.campaignExport()?.showDeliverableStatus,
  );

  public readonly briefingDeliverableLabels = computed<string>(() => {
    const names = this.tileConfiguration().deliverableTypeNames;
    const items = names.length <= 4 ? names : names.slice(0, 3);
    const parts = [...items];

    if (names.length > 4) {
      parts.push(`${names.length - 3} more`);
    }

    return parts.join(' - ');
  });

  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly mediaUtils = inject(MediaUtilitiesService);
  private readonly socialContentTileService = inject(SocialContentTileService);
  private readonly textClampUtilitiesService = inject(TextClampUtilitiesService);
  private readonly translate = inject(TranslateService);

  public readonly executionTimeDisplay = computed<string>(() => {
    const config = this.tileConfiguration();

    if (config.content) {
      if (!config.executionDateTime) {
        return '';
      }

      const parsed = this.dateUtils.parseDateTimeFromApi(config.executionDateTime);

      if (!parsed) {
        return '';
      }

      return this.formatDateWithRelativeLabel(parsed, config.isDateOnlyExecution);
    }

    const { executionType } = config;

    if (executionType === EExecutionType.always) {
      return this.translate.instant('shared.tile.alwaysRequired');
    }

    if (executionType === EExecutionType.undesignated) {
      const order = config.undesignatedExecutionOrder;

      return order
        ? `${this.translate.instant('shared.tile.anyTime')} (${order}.)`
        : this.translate.instant('shared.tile.anyTime');
    }

    if (config.executionDateTime) {
      const parsed = this.dateUtils.parseDateTimeFromApi(config.executionDateTime);

      if (parsed) {
        return this.formatDateWithRelativeLabel(parsed, config.isDateOnlyExecution);
      }
    }

    return '';
  });

  public readonly platformFeatureLineClampValues = computed(() => {
    return this.socialContentTileService.getPlatformFeatureLineClampValues(
      this.tileConfiguration().platformFeature,
      !!this.tileConfiguration().media?.length,
    );
  });

  constructor() {
    this.createClampStateEffect();
  }

  // Re-measures clamp state whenever the rendered element becomes available (initial render -
  // creatorNameElement()/titleElement()/descriptionElement() are themselves reactive viewChild
  // signals, so this subsumes the old one-shot ngAfterViewInit measurement) AND whenever
  // tileConfiguration() changes. That second trigger matters because a tile can now persist
  // across a data refresh instead of being torn down and rebuilt (see
  // SocialContentTilesListComponent's showInitialSkeleton) - if the same tile.id's title,
  // description, or creator name text changes length under the same DOM node, isTitleClamped()/
  // isDescriptionClamped() (which gate the tooltips in the template) would otherwise be left
  // stale from whatever was measured at mount.
  private createClampStateEffect(): void {
    effect(() => {
      const creatorNameElement = this.creatorNameElement();
      const titleElement = this.titleElement();
      const descriptionElement = this.descriptionElement();

      this.tileConfiguration();

      untracked(() => {
        requestAnimationFrame(() => {
          this.isCreatorNameClamped.set(this.textClampUtilitiesService.isTextClamped(creatorNameElement));
          this.isTitleClamped.set(this.textClampUtilitiesService.isTextClamped(titleElement));
          this.isDescriptionClamped.set(this.textClampUtilitiesService.isTextClamped(descriptionElement));
        });
      });
    });
  }

  public onTileClick(): void {
    if (!this.clickable()) {
      return;
    }

    this.tileClicked.emit(this.tileConfiguration());
  }

  public onCarouselClick(event: Event): void {
    event.stopPropagation();
  }

  private formatDateWithRelativeLabel(date: Date, isDateOnly: boolean | null | undefined): string {
    const relativeLabel = this.getRelativeDayLabel(date);

    if (relativeLabel) {
      if (isDateOnly) {
        return relativeLabel;
      }

      return `${relativeLabel} ${new DatePipe('en').transform(date, 'HH:mm') ?? ''}`.trim();
    }

    return isDateOnly
      ? (new DatePipe('en').transform(date, 'dd/MM/yyyy') ?? '')
      : (new DatePipe('en').transform(date, 'dd/MM/yyyy HH:mm') ?? '');
  }

  private getRelativeDayLabel(date: Date): string | null {
    const diffDays = differenceInCalendarDays(date, new Date());

    if (diffDays === -1) return this.translate.instant('shared.tile.yesterday') as string;

    if (diffDays === 0) return this.translate.instant('shared.tile.today') as string;

    if (diffDays === 1) return this.translate.instant('shared.tile.tomorrow') as string;

    return null;
  }
}
